import {observer} from 'mobx-react';
import {useRef, useCallback, useState, useLayoutEffect} from 'react';
import {alpha, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Frame} from '../common/Frame';
import {VideoPlayerState, getTimePosition, getTimeFromPosition, formatTime} from './useVideoPlayerState';
import {recording} from '../../proto/compiled';
import {D} from '../../Constants';

interface VideoPlayerTimelineProps {
  state: VideoPlayerState;
  annotations?: recording.IRecordingAnnotation[];
  selectedAnnotation?: recording.IRecordingAnnotation;
  editMode?: boolean;
  onSeek: (timeMs: number) => void;
  onAnnotationSelect?: (annotation: recording.IRecordingAnnotation | undefined) => void;
  onAnnotationBoundsChange?: (annotation: recording.IRecordingAnnotation, startMs: number, endMs: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

export const VideoPlayerTimeline = observer(({
  state,
  annotations,
  selectedAnnotation,
  editMode,
  onSeek,
  onAnnotationSelect,
  onAnnotationBoundsChange,
  onScrubStart,
  onScrubEnd,
}: VideoPlayerTimelineProps) => {
  const theme = useTheme();
  const timelineRef = useRef<HTMLDivElement>(null);

  const getAnnotationColor = (annotation: recording.IRecordingAnnotation) => {
    switch (annotation.annotationType) {
      case recording.AnnotationType.AT_GOOD_PRACTICE:
        return theme.palette.success.main;
      case recording.AnnotationType.AT_COMMON_MISTAKE:
        return theme.palette.error.main;
      default:
        return theme.palette.info.main;
    }
  };

  const handleTimelinePointerDown = useCallback((e: React.PointerEvent) => {
    if (!timelineRef.current || state.isDraggingHandle) return;
    e.preventDefault();

    state.setDraggingTimeline(true);
    onScrubStart?.();

    // Immediately seek to clicked position
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const timeMs = getTimeFromPosition(percent, state.durationMs);
    onSeek(timeMs);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const timeMs = getTimeFromPosition(percent, state.durationMs);
      onSeek(timeMs);
    };

    const handlePointerUp = () => {
      state.setDraggingTimeline(false);
      onScrubEnd?.();
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [state, onSeek, onScrubStart, onScrubEnd]);

  const handleMarkerClick = useCallback((e: React.MouseEvent, annotation: recording.IRecordingAnnotation) => {
    e.stopPropagation();
    onAnnotationSelect?.(annotation);
  }, [onAnnotationSelect]);

  return (
    <Frame
      ref={timelineRef}
      clickable
      style={{
        position: 'relative',
        height: 24,
        cursor: 'pointer',
      }}
      onPointerDown={handleTimelinePointerDown}
    >
      {/* Track background */}
      <Frame
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 4,
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderRadius: 2,
        }}
      />

      {/* Selected annotation range highlight */}
      {selectedAnnotation && selectedAnnotation.endMs && selectedAnnotation.endMs > (selectedAnnotation.startMs ?? 0) && (() => {
        const startPos = state.isDraggingHandle && state.draggingHandleType === 'start'
          ? state.draggingHandlePosition
          : getTimePosition(selectedAnnotation.startMs ?? 0, state.durationMs);
        const endPos = state.isDraggingHandle && state.draggingHandleType === 'end'
          ? state.draggingHandlePosition
          : getTimePosition(selectedAnnotation.endMs, state.durationMs);
        return (
          <Frame
            noTransition
            style={{
              position: 'absolute',
              left: `${Math.min(startPos, endPos)}%`,
              width: `${Math.abs(endPos - startPos)}%`,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 14,
              backgroundColor: alpha(getAnnotationColor(selectedAnnotation), 0.8),
              borderRadius: 7,
              pointerEvents: 'none',
              zIndex: 2,
              transition: 'none',
            }}
          />
        );
      })()}

      {/* Progress bar */}
      <Frame
        noTransition
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 4,
          width: `${state.progressPercent}%`,
          backgroundColor: 'white',
          borderRadius: 2,
          pointerEvents: 'none',
          transition: 'none',
          zIndex: 3,
        }}
      />

      {/* Scrubber thumb */}
      <Frame
        noTransition
        style={{
          position: 'absolute',
          left: `${state.progressPercent}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: state.isDraggingTimeline ? 12 : 10,
          height: state.isDraggingTimeline ? 12 : 10,
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'grab',
          zIndex: 6,
          pointerEvents: 'none',
          transition: 'none',
        }}
      />

      {/* Annotation markers - hide others when editing */}
      {annotations?.flatMap((annotation, index) => {
        const isSelected = annotation === selectedAnnotation;
        const isHovered = annotation === state.hoveredAnnotation;
        const color = getAnnotationColor(annotation);

        // Hide non-selected annotations when in edit mode
        if (editMode && !isSelected) return [];

        const markers = [
          // Start marker
          <AnnotationMarker
            key={`start-${annotation.id || index}`}
            type="start"
            annotation={annotation}
            isSelected={isSelected}
            isHovered={isHovered}
            color={color}
            state={state}
            timelineRef={timelineRef}
            editMode={editMode && isSelected}
            onMarkerClick={handleMarkerClick}
            onBoundsChange={onAnnotationBoundsChange}
            onSeek={onSeek}
            onScrubStart={onScrubStart}
            onScrubEnd={onScrubEnd}
          />,
        ];

        // End marker (only when selected)
        if (annotation.endMs && annotation.endMs > 0 && isSelected) {
          markers.push(
            <AnnotationMarker
              key={`end-${annotation.id || index}`}
              type="end"
              annotation={annotation}
              isSelected={isSelected}
              isHovered={isHovered}
              color={color}
              state={state}
              timelineRef={timelineRef}
              editMode={editMode && isSelected}
              onMarkerClick={handleMarkerClick}
              onBoundsChange={onAnnotationBoundsChange}
              onSeek={onSeek}
              onScrubStart={onScrubStart}
              onScrubEnd={onScrubEnd}
            />
          );
        }

        return markers;
      })}

      {/* Tooltip for hovered annotation */}
      {state.hoveredAnnotation && (
        <TimelineTooltip
          annotation={state.hoveredAnnotation}
          position={getTimePosition(
            state.hoveredMarkerType === 'end'
              ? (state.hoveredAnnotation.endMs ?? 0)
              : (state.hoveredAnnotation.startMs ?? 0),
            state.durationMs
          )}
        />
      )}
    </Frame>
  );
});

// Annotation marker component with optional drag functionality
interface AnnotationMarkerProps {
  type: 'start' | 'end';
  annotation: recording.IRecordingAnnotation;
  isSelected: boolean;
  isHovered: boolean;
  color: string;
  state: VideoPlayerState;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  editMode?: boolean;
  onMarkerClick: (e: React.MouseEvent, annotation: recording.IRecordingAnnotation) => void;
  onBoundsChange?: (annotation: recording.IRecordingAnnotation, startMs: number, endMs: number) => void;
  onSeek?: (timeMs: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

const AnnotationMarker = observer(({
  type,
  annotation,
  isSelected,
  isHovered,
  color,
  state,
  timelineRef,
  editMode,
  onMarkerClick,
  onBoundsChange,
  onSeek,
  onScrubStart,
  onScrubEnd,
}: AnnotationMarkerProps) => {
  const timeMs = type === 'start' ? (annotation.startMs ?? 0) : (annotation.endMs ?? 0);
  const position = getTimePosition(timeMs, state.durationMs);
  const isDraggingThis = state.isDraggingHandle && state.draggingHandleType === type;

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Always stop propagation so timeline doesn't also handle this as a seek
    e.stopPropagation();

    if (!editMode) return;

    e.preventDefault();
    state.setDraggingHandle(true, type);
    state.setDraggingHandlePosition(position);
    onScrubStart?.();
    // Seek to marker position on click
    onSeek?.(timeMs);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      state.setDraggingHandlePosition(percent);
      // Seek video to show current position
      const seekTimeMs = getTimeFromPosition(percent, state.durationMs);
      onSeek?.(seekTimeMs);
    };

    const handlePointerUp = () => {
      const newTimeMs = getTimeFromPosition(state.draggingHandlePosition, state.durationMs);
      const startMs = annotation.startMs ?? 0;
      const endMs = annotation.endMs ?? 0;

      if (type === 'start') {
        // Ensure start doesn't exceed end
        const constrainedStart = endMs > 0 ? Math.min(newTimeMs, endMs - 1000) : newTimeMs;
        onBoundsChange?.(annotation, Math.max(0, constrainedStart), endMs);
      } else {
        // Ensure end doesn't go before start
        const constrainedEnd = Math.max(newTimeMs, startMs + 1000);
        onBoundsChange?.(annotation, startMs, Math.min(constrainedEnd, state.durationMs));
      }

      state.setDraggingHandle(false);
      onScrubEnd?.();
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [type, annotation, state, timelineRef, position, onBoundsChange, editMode, onSeek, onScrubStart, onScrubEnd]);

  const displayPosition = isDraggingThis ? state.draggingHandlePosition : position;
  const size = isSelected || isHovered || isDraggingThis ? 16 : 12;

  return (
    <Frame
      clickable
      noTransition
      style={{
        position: 'absolute',
        left: `${displayPosition}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        border: isSelected ? '2px solid white' : undefined,
        boxShadow: isSelected ? `0 0 0 2px ${color}` : '0 1px 3px rgba(0,0,0,0.3)',
        cursor: editMode ? 'ew-resize' : 'pointer',
        zIndex: isSelected ? 5 : 4,
        transition: 'none',
      }}
      onClick={(e) => {
        onMarkerClick(e, annotation);
        // Seek to marker position when clicked
        onSeek?.(timeMs);
      }}
      onPointerDown={handlePointerDown}
      onMouseEnter={() => state.setHoveredAnnotation(annotation, type)}
      onMouseLeave={() => state.setHoveredAnnotation(undefined)}
    />
  );
});

// Tooltip component
interface TimelineTooltipProps {
  annotation: recording.IRecordingAnnotation;
  position: number;
}

const TimelineTooltip = ({annotation, position}: TimelineTooltipProps) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [shiftPx, setShiftPx] = useState(0);

  // Centering on the annotation position can push the bubble past the
  // track edges (clipped by the player's overflow:hidden) - measure and
  // shift it back so it stops at the edge instead.
  useLayoutEffect(() => {
    const el = ref.current;
    const track = el?.offsetParent as HTMLElement | null;
    if (!el || !track) return;
    const centeredLeft = (position / 100) * track.offsetWidth - el.offsetWidth / 2;
    const clampedLeft = Math.max(0, Math.min(centeredLeft, track.offsetWidth - el.offsetWidth));
    setShiftPx(clampedLeft - centeredLeft);
  }, [position, annotation]);

  return (
    <Frame
      ref={ref}
      direction="column"
      padding={1}
      radius={1}
      style={{
        position: 'absolute',
        left: `${position}%`,
        bottom: '100%',
        transform: `translateX(calc(-50% + ${shiftPx}px))`,
        marginBottom: D.unit(1),
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
        minWidth: 100,
        maxWidth: 200,
        zIndex: 20,
        pointerEvents: 'none',
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap>
        {annotation.label || 'Untitled'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatTime(annotation.startMs ?? 0)}
        {annotation.endMs && annotation.endMs > (annotation.startMs ?? 0) && (
          <> - {formatTime(annotation.endMs)}</>
        )}
      </Typography>
    </Frame>
  );
};
